import { useState } from 'react';
import { ChevronDown, ChevronRight, History } from 'lucide-react';
import { PhoneIcon } from 'assets/icons';
import ListIdentity from 'components/record/ListIdentity';
import { SkeletonLine } from 'components/skeleton/Skeleton';
import Tag from 'components/crm/ui/Tag';
import useText from 'hooks/useText';
import SectionHeader from './SectionHeader';
import { addDays, dayLabel, durationLabel, hourLabel, isoDay } from 'helper/timeLabels';
import { CALL_TIME_ZONE } from 'pages/prospection/callTime';

// The agency works on Martinique time, whatever the browser's zone: sessions,
// their days and their calls are all shown in it.
const martiniqueParts = new Intl.DateTimeFormat('en-US', {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
  timeZone: CALL_TIME_ZONE,
});

// A local Date whose fields read the Martinique wall clock, for the local
// helpers of timeLabels (dayLabel, hourLabel, isoDay).
export const martiniqueWall = (value) => {
  const parts = Object.fromEntries(
    martiniqueParts.formatToParts(new Date(value)).map(({ type, value: part }) => [type, part]),
  );
  return new Date(+parts.year, +parts.month - 1, +parts.day, +parts.hour, +parts.minute);
};

// « lun. 26 oct. à 10 h 55 »: when a session started.
export const sessionWhen = (session) => {
  const at = martiniqueWall(session.startedAt);
  return `${dayLabel(at)} à ${hourLabel(at)}`;
};

const callClock = new Intl.DateTimeFormat('fr-FR', {
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
  timeZone: CALL_TIME_ZONE,
});

const pad = (value) => String(value).padStart(2, '0');

// « 2 min 05 s », « 1 h 02 min 05 s », or « — » when the length is unknown.
export const secondsLabel = (text, seconds) => {
  if (seconds === null || seconds === undefined || !Number.isFinite(seconds)) {
    return text('sessions.unknownDuration');
  }
  const total = Math.max(0, Math.round(seconds));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const rest = pad(total % 60);
  return hours
    ? text('sessions.hoursSeconds', { hours, minutes: pad(minutes), seconds: rest })
    : text('sessions.seconds', { minutes, seconds: rest });
};

// The day a group of sessions goes under: « Aujourd'hui », « Hier », or its date.
const dayHeading = (day, now, text) => {
  if (day === isoDay(now)) return text('sessions.today');
  if (day === isoDay(addDays(now, -1))) return text('sessions.yesterday');
  return dayLabel(new Date(`${day}T12:00:00`));
};

// The sessions of each day, the latest day first, as the api orders them.
const byDay = (sessions) =>
  sessions.reduce((groups, session) => {
    const day = isoDay(martiniqueWall(session.startedAt));
    const group = groups.find((one) => one.day === day);
    if (group) group.sessions.push(session);
    else groups.push({ day, sessions: [session] });
    return groups;
  }, []);

// One call of a session: its time, the company called and its contact
// (`withCompany`: the summary's list), its length (`withDuration`: the agency
// manager's list, which knows it), its result and its note. Only « Sans
// réponse » is a badge, so that the unanswered calls stand out.
export const SessionCallRow = ({ call, text, withCompany = false, withDuration = true }) => {
  const result = call.outcome ? text(`results.${call.outcome}`) || call.outcome : '';
  return (
    <li data-testid="session-call" className="flex items-start gap-3 py-2 text-xs leading-4">
      <span className="w-10 shrink-0 font-medium text-ink">{callClock.format(new Date(call.at))}</span>
      {withCompany && (
        <span className="flex w-36 min-w-0 shrink-0 flex-col gap-0.5">
          <span className="truncate font-medium text-ink">{call.company?.name ?? '-'}</span>
          {call.contactName && <span className="truncate text-muted">{call.contactName}</span>}
        </span>
      )}
      {withDuration && <span className="w-24 shrink-0 text-muted">{secondsLabel(text, call.durationSeconds)}</span>}
      <span className="w-28 shrink-0">
        {call.outcome === 'no_answer' ? <Tag tone="neutral">{result}</Tag> : <span className="text-body">{result}</span>}
      </span>
      {call.note && <span className="min-w-0 flex-1 whitespace-pre-line break-words text-body">{call.note}</span>}
    </li>
  );
};

// A session as a row of the record pages (« Entreprises du groupe »): when
// it began, its calls and the contacts reached, how long it lasted on the
// right; one stopped before any call says so. A session read with its calls
// (the agency manager's view) also gives their count and total length, and
// unfolds to list them.
const SessionRow = ({ session, onOpen, text, plural }) => {
  const [unfolded, setUnfolded] = useState(false);
  const stats = session.stats ?? {};
  const calls = stats.calls ?? 0;
  const reached = stats.reached ?? 0;
  const minutes = stats.seconds ? Math.max(1, Math.round(stats.seconds / 60)) : null;
  const callLog = Array.isArray(session.calls) ? session.calls : null;
  return (
    <div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => onOpen(session)}
          className="-mx-2 flex min-w-0 flex-1 items-center gap-3 rounded-lg px-2 py-2.5 text-left transition-colors hover:bg-gray-50"
        >
          <span className="min-w-0 flex-1">
            <ListIdentity
              icon={PhoneIcon}
              name={hourLabel(martiniqueWall(session.startedAt))}
              detail={
                calls
                  ? `${plural('sessions.made', calls, { planned: session.planned ?? calls })} · ${plural('sessions.reached', reached)}`
                  : text('sessions.noCall')
              }
            />
          </span>
          {minutes && <span className="shrink-0 text-xs text-gray-600">{durationLabel(minutes)}</span>}
          <ChevronRight size={16} className="shrink-0 text-gray-400" />
        </button>
        {callLog && (
          <button
            type="button"
            onClick={() => setUnfolded((open) => !open)}
            aria-expanded={unfolded}
            aria-label={text(unfolded ? 'sessions.hideCalls' : 'sessions.showCalls')}
            data-testid="session-calls-toggle"
            className="flex shrink-0 items-center gap-1.5 rounded-lg px-2 py-1.5 text-xs text-gray-600 transition-colors hover:bg-gray-50"
          >
            <span>
              {plural('sessions.callCount', session.callCount ?? callLog.length)} ·{' '}
              {secondsLabel(text, session.totalDurationSeconds)}
            </span>
            <ChevronDown size={14} className={`text-gray-400 transition-transform ${unfolded ? 'rotate-180' : ''}`} />
          </button>
        )}
      </div>
      {callLog && unfolded && (
        <div className="mb-2 rounded-lg bg-gray-50 px-3">
          {callLog.length ? (
            <ul className="divide-y divide-gray-100">
              {callLog.map((call, index) => (
                <SessionCallRow key={`${call.at}-${index}`} call={call} text={text} />
              ))}
            </ul>
          ) : (
            <p className="py-2 text-xs text-muted">{text('sessions.noCalls')}</p>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * One's past call sessions, in the right column while nothing is called,
 * under the day they were held (SessionRow). A click opens its summary
 * (`onOpen(session)`). `sessions`: null while they are read. `title` and
 * `emptyText` replace one's own wording (another's sessions, read-only).
 * Sessions carrying `calls` (read by the agency manager) unfold, read-only.
 */
const SessionHistory = ({ sessions, onOpen, title, emptyText }) => {
  const { text, plural } = useText('prospection');
  const now = martiniqueWall(new Date());
  return (
    <section className="flex h-full min-w-0 flex-1 flex-col rounded-2xl border border-line bg-white">
      <div className="flex shrink-0 items-center gap-2 p-6 pb-4">
        <History size={18} className="text-muted" />
        <h2 className="font-title text-lg font-semibold leading-6 text-ink">{title ?? text('sessions.title')}</h2>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto px-6 pb-6">
        {sessions === null && (
          <div role="status" aria-busy="true" className="flex flex-col">
            <SkeletonLine className="mb-2 mt-2 h-3 w-24" />
            {Array.from({ length: 4 }, (_, index) => (
              <div key={index} className="flex items-center gap-3 border-b border-gray-100 py-2.5 last:border-b-0">
                <span aria-hidden="true" className="size-8 shrink-0 animate-pulse rounded-md bg-gray-200" />
                <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                  <SkeletonLine className={index % 2 ? 'w-32' : 'w-40'} />
                  <SkeletonLine className="h-3 w-24" />
                </div>
                <SkeletonLine className="h-3 w-12" />
              </div>
            ))}
          </div>
        )}
        {sessions?.length === 0 && <p className="py-4 text-sm text-muted">{emptyText ?? text('sessions.empty')}</p>}
        {sessions?.length > 0 && (
          <div className="flex flex-col">
            {byDay(sessions).map((group) => (
              <section key={group.day}>
                <SectionHeader title={dayHeading(group.day, now, text)} count={group.sessions.length} />
                <div className="divide-y divide-gray-100">
                  {group.sessions.map((session) => (
                    <SessionRow key={session.id} session={session} onOpen={onOpen} text={text} plural={plural} />
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default SessionHistory;
